import { useMemo, useState } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Card, Checkbox, Tabs, TabsContent, TabsList, TabsTrigger } from '@design-system';
import { Tooltip } from '@mui/material';
import { ArrowRight, Check, TrendingUp } from 'lucide-react';
import { DataTable } from '@client/components/DataTable';
import { SortHeader, renderHeaderCell, renderTableCell } from '@client/components/DataTableColumn';
import { ReturnOnInvestmentCalculationOptions } from '@client/components/ReturnOnInvestmentCalculationOptions';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { selectClient, selectClientCalculationSettings } from '@client/model';
import { formatROI, IRR_SUSPECT_EXPLANATION, IRR_SUSPECT_MESSAGE } from '@shared';
import type { GenworthCoverageSnapshot, GenworthRateActionLetterResults } from '@shared';
import { BulkActionBar } from './BulkActionBar';
import { useApplyRateActionOption } from './hooks/useApplyRateActionOption';
import { useRateActionProjections } from './hooks/useRateActionProjections';

interface RateActionSummaryProps {
  resultsJson: string;
  requestId: string;
  clientId: string;
  appliedFundingSourceIds: string | undefined;
}

interface ComparisonRow {
  id: string;
  label: string;
  sublabel?: string;
  coverage: GenworthCoverageSnapshot;
  /** null = synthesized "true original" row (not applyable) */
  optionIndex: number | null;
  alreadyAppliedFundingSourceId?: string;
  /** Populated for non-NONFORFEITURE rows + the synthetic original; null when not derivable. */
  derived: PolicyFundingSource | null;
}

const columnHelper = createColumnHelper<ComparisonRow>();
type ViewMode = 'details' | 'projections';

export function RateActionSummary({
  resultsJson,
  requestId,
  clientId,
  appliedFundingSourceIds,
}: RateActionSummaryProps) {
  const parsed = useMemo(() => safeParse(resultsJson), [resultsJson]);
  const appliedMap = useMemo(
    () => parseAppliedMap(appliedFundingSourceIds),
    [appliedFundingSourceIds],
  );

  const client = useSelector(selectClient);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const navigate = useNavigate();
  const [roiCalcType, setRoiCalcType] =
    useState<ReturnOnInvestmentCalculationType>('internalRateOfReturn');

  const projections = useRateActionProjections({ results: parsed, client, requestId });

  const applyMutation = useApplyRateActionOption({ clientId, requestId });

  const [viewMode, setViewMode] = useState<ViewMode>('details');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Bumped on Clear / successful Apply to remount the DataTable and drop its internal
  // rowSelection (DataTable doesn't expose controlled selection). Sort/filters/pagination
  // are persisted via localStorage + Redux so they survive the remount.
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  if (!parsed) {
    return (
      <Card className="p-6">
        <p className="text-red-600">Could not parse extraction results.</p>
        <pre className="mt-3 overflow-auto text-xs text-gray-600">{resultsJson}</pre>
      </Card>
    );
  }

  const { metadata } = parsed;
  const rows = buildComparisonRows(parsed, appliedMap, projections);

  // Applied options stay actionable so they can be reapplied (each apply creates a fresh
  // funding source). Only the synthetic "true original" anchor is non-actionable.
  const actionableRows = rows.filter(r => r.optionIndex != null);
  const actionableSelected = actionableRows.filter(r => selectedIds.has(r.id));
  const hasAppliedRows = rows.some(r => r.alreadyAppliedFundingSourceId);

  const selectColumn = buildSelectColumn();
  const detailsColumns = [selectColumn, ...buildDetailsColumns()];
  const projectionsColumns = [selectColumn, ...buildProjectionsColumns(roiCalcType)];

  function handleSelectionChange(newRows: ComparisonRow[]) {
    setSelectedIds(new Set(newRows.map(r => r.id)));
  }

  function handleClearSelection() {
    setSelectedIds(new Set());
    setSelectionResetKey(k => k + 1);
    setBulkError(null);
  }

  async function handleBulkApply() {
    if (actionableSelected.length === 0) return;
    setBulkApplying(true);
    setBulkError(null);
    try {
      // Sequential, not parallel: the server mutates appliedFundingSourceIds JSON on the
      // same row for each option, and parallel writes would race on that field.
      for (const row of actionableSelected) {
        if (row.optionIndex == null) continue;
        await applyMutation.mutateAsync({ requestId, optionIndex: row.optionIndex });
      }
      handleClearSelection();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to apply selected options');
    } finally {
      setBulkApplying(false);
    }
  }

  // Banner conditions for the Projections tab: either intake is incomplete (all derived
  // rows zero out the calc) or `premiumBeforeIncrease` wasn't extracted (synthetic "true
  // original" row is suppressed). Either way the user gets dashes instead of numbers; tell
  // them why. trueOriginalSuppressed is driven by the metadata field directly so the copy
  // accurately reflects the underlying cause.
  const projectionsAvailable = projections.some(p => p.derived?.policyTotalCost != null);
  const trueOriginalSuppressed = metadata.premiumBeforeIncrease == null;
  // On-claim clients are blocked from projections — see useRateActionProjections for the
  // calc-engine rationale. The tab stays clickable so the feature remains discoverable.
  const clientOnClaim = client?.intakeSurvey?.clientHasStartedLtc === true;

  return (
    <>
      <div className="space-y-3 pb-24">
        <div className="space-y-2">
          {metadata.carrier && (
            <div className="text-darkestPurple text-lg font-semibold">{metadata.carrier}</div>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
            {metadata.noticeDate && (
              <div>
                <span className="text-gray-500">Notice </span>
                <span className="font-medium">{fmtNiceDate(metadata.noticeDate)}</span>
              </div>
            )}
            {metadata.premiumEffectiveDate && (
              <div>
                <span className="text-gray-500">Effective </span>
                <span className="font-medium">{fmtNiceDate(metadata.premiumEffectiveDate)}</span>
                {metadata.percentageIncrease != null && (
                  <span className="text-gray-500">
                    {' '}
                    ({metadata.percentageIncrease > 0 ? '+' : ''}
                    {metadata.percentageIncrease.toFixed(1)}% premium increase)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <Tabs
          value={viewMode}
          onValueChange={v => setViewMode(v as ViewMode)}
          className="mt-8 gap-0"
        >
          <div className="flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="details">Option details</TabsTrigger>
              <TabsTrigger
                value="projections"
                className="gap-1.5"
              >
                <TrendingUp className="size-4" />
                Projections
              </TabsTrigger>
            </TabsList>
            {viewMode === 'projections' && !clientOnClaim && (
              <ReturnOnInvestmentCalculationOptions
                discountRate={clientCalculationSettings?.discountRate}
                calculationType={roiCalcType}
                onChange={setRoiCalcType}
                isButtonStyle
              />
            )}
          </div>
          <TabsContent
            value="details"
            className="mt-0"
          >
            <DataTable
              key={`details-${selectionResetKey}`}
              columns={detailsColumns as ColumnDef<ComparisonRow>[]}
              data={rows}
              getRowId={r => r.id}
              tableId="rateActionComparisonDetails"
              variant="outlined"
              hideColumnSelector
              onSelectionChange={handleSelectionChange}
              defaultSelection={rows.filter(r => selectedIds.has(r.id))}
            />
          </TabsContent>
          <TabsContent
            value="projections"
            className="mt-0 space-y-3"
          >
            {clientOnClaim ? (
              <Card className="mt-4 border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-800">
                  Projections aren&apos;t available for clients currently receiving long-term
                  care. Option details are still shown for comparison.
                </p>
              </Card>
            ) : (
              <>
                {!projectionsAvailable && (
                  <Card className="border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm text-amber-800">
                      Projections aren&apos;t available — the client&apos;s intake is missing
                      inputs the calculator needs (age, ZIP, etc.). Complete intake to see Cost /
                      Payout / ROI.
                    </p>
                  </Card>
                )}
                {projectionsAvailable && trueOriginalSuppressed && (
                  <Card className="border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm text-amber-800">
                      Pre-increase premium wasn&apos;t extracted from the letter, so the
                      &ldquo;true original&rdquo; comparison anchor row is hidden in this view.
                    </p>
                  </Card>
                )}
                {projectionsAvailable && (
                  <Card className="mt-4 border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Historic premiums:</span> Premiums the
                      policyholder has already paid aren&apos;t included in these projections —
                      Cost and ROI compare options as if the policy started today. Historic-premium
                      modeling is coming soon.
                    </p>
                  </Card>
                )}
                <DataTable
                  key={`projections-${selectionResetKey}`}
                  columns={projectionsColumns as ColumnDef<ComparisonRow>[]}
                  data={rows}
                  getRowId={r => r.id}
                  tableId="rateActionComparisonProjections"
                  variant="outlined"
                  hideColumnSelector
                  onSelectionChange={handleSelectionChange}
                  defaultSelection={rows.filter(r => selectedIds.has(r.id))}
                />
              </>
            )}
          </TabsContent>
        </Tabs>

        {hasAppliedRows && (
          <div className="flex justify-end pt-2">
            <Button onClick={() => navigate(`/clients/${clientId}/onboarding/funding-sources`)}>
              View funding plan
              <ArrowRight className="ml-1 size-4" />
            </Button>
          </div>
        )}

        <div className="mt-8 rounded-xl bg-blue-50 p-4">
          <p className="text-xs text-gray-950">
            <span className="font-semibold">Disclaimer:</span> Projected outcomes assume each option
            is used as if it is the only LTC coverage in place; rate-action options are modeled
            independently and not combined together. The extraction, modeling, and calculation
            service is provided &ldquo;AS IS&rdquo; — Waterlily does not guarantee accuracy, and you
            are responsible for verifying and correcting the extracted values and calculations. See{' '}
            <a
              className="underline"
              href="https://www.waterlily.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            for more details and disclaimers.
          </p>
        </div>
      </div>

      {actionableSelected.length > 0 && (
        <BulkActionBar
          count={actionableSelected.length}
          applying={bulkApplying}
          error={bulkError}
          onApply={handleBulkApply}
          onClear={handleClearSelection}
        />
      )}
    </>
  );
}

function buildComparisonRows(
  parsed: GenworthRateActionLetterResults,
  appliedMap: Record<string, string>,
  projections: ReturnType<typeof useRateActionProjections>,
): ComparisonRow[] {
  const { metadata, currentCoverage, options } = parsed;
  const projectionByKey = indexProjections(projections);
  const rows: ComparisonRow[] = [];

  const keepCurrent = options.find(o => o.optionType === 'KEEP_CURRENT');
  if (!keepCurrent) {
    console.warn('RateActionSummary: no KEEP_CURRENT option found; comparison may be incomplete');
  }
  const benefitsForOriginal = keepCurrent ?? currentCoverage;

  // Synthesized "true original" — same benefits, pre-increase premium. Always rendered so
  // a missing premiumBeforeIncrease is visible (— in the cell) instead of silently dropped.
  rows.push({
    id: 'true-original',
    label: 'Your Current Benefits',
    sublabel: 'Current premium (before increase)',
    coverage: {
      ...benefitsForOriginal,
      annualPremium: metadata.premiumBeforeIncrease,
    },
    optionIndex: null,
    derived: projectionByKey.get('null') ?? null,
  });

  options.forEach((opt, i) => {
    rows.push({
      id: `opt-${i}`,
      label: opt.carrierLabel,
      sublabel: opt.optionType === 'KEEP_CURRENT' ? 'Same benefits, new premium' : undefined,
      coverage: opt,
      optionIndex: i,
      alreadyAppliedFundingSourceId: appliedMap[String(i)],
      derived: projectionByKey.get(String(i)) ?? null,
    });
  });

  return rows;
}

function indexProjections(
  projections: ReturnType<typeof useRateActionProjections>,
): Map<string, PolicyFundingSource> {
  // Key by "null" for the synthetic original, stringified index for everything else.
  const map = new Map<string, PolicyFundingSource>();
  projections.forEach(p => {
    const key = p.optionIndex == null ? 'null' : String(p.optionIndex);
    map.set(key, p.derived);
  });
  return map;
}

// ---------- columns ----------

function buildSelectColumn() {
  return columnHelper.display({
    id: 'select',
    meta: { label: '' },
    size: 40,
    header: () => renderHeaderCell(<span className="sr-only">Select</span>),
    cell: info => {
      const row = info.row.original;
      // Synthetic "true original" anchor isn't applyable. Already-applied options keep their
      // checkbox so the user can reapply (creating a fresh funding source); the "Applied"
      // badge under the option label stays visible alongside it.
      if (row.optionIndex == null) {
        return renderTableCell(null);
      }
      return renderTableCell(
        <Checkbox
          checked={info.row.getIsSelected()}
          onCheckedChange={v => info.row.toggleSelected(v === true)}
          aria-label={`Select ${row.label}`}
        />,
      );
    },
  });
}

function renderOptionLabelCell(row: ComparisonRow) {
  return renderTableCell(
    <div className="flex flex-col items-start">
      <span className="font-medium">{row.label}</span>
      {row.sublabel && <span className="mt-0.5 text-xs text-gray-500">{row.sublabel}</span>}
      {row.alreadyAppliedFundingSourceId && (
        <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
          <Check className="size-3" /> Applied
        </span>
      )}
    </div>,
  );
}

function buildDetailsColumns() {
  return [
    columnHelper.accessor('label', {
      id: 'label',
      meta: { label: 'Option' },
      size: 240,
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Option
          </SortHeader>,
        ),
      cell: info => renderOptionLabelCell(info.row.original),
    }),
    columnHelper.accessor(r => r.coverage.annualPremium, {
      id: 'annualPremium',
      meta: { label: 'Annual premium' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Annual premium
          </SortHeader>,
        ),
      cell: info => renderTableCell(fmtCurrency(info.getValue())),
    }),
    columnHelper.accessor(r => r.coverage.dailyPaymentMaximum, {
      id: 'dailyPaymentMaximum',
      meta: { label: 'Daily max' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Daily max
          </SortHeader>,
        ),
      cell: info => renderTableCell(fmtCurrency(info.getValue())),
    }),
    columnHelper.accessor(r => r.coverage.remainingLifetimePaymentMaximum, {
      id: 'lifetimeMax',
      meta: { label: 'Lifetime max' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Lifetime max
          </SortHeader>,
        ),
      cell: info => {
        const c = info.row.original.coverage;
        return renderTableCell(
          fmtCurrencyOrUnlimited(c.remainingLifetimePaymentMaximum, c.remainingLifetimePaymentMaximumIsUnlimited),
        );
      },
    }),
    columnHelper.accessor(r => r.coverage.benefitPeriodYears, {
      id: 'benefitPeriod',
      meta: { label: 'Benefit period' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Benefit period
          </SortHeader>,
        ),
      cell: info => {
        const c = info.row.original.coverage;
        return renderTableCell(fmtYearsOrUnlimited(c.benefitPeriodYears, c.benefitPeriodIsUnlimited));
      },
    }),
    columnHelper.accessor(r => formatInflationSortKey(r.coverage.inflationProtection), {
      id: 'inflation',
      meta: { label: 'Inflation' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Inflation
          </SortHeader>,
        ),
      cell: info => renderTableCell(fmtInflation(info.row.original.coverage.inflationProtection)),
    }),
    columnHelper.accessor(r => r.coverage.facilityEliminationPeriodDays, {
      id: 'eliminationPeriod',
      meta: { label: 'Elim. period (Facility / Home)' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Elim. period (F / H)
          </SortHeader>,
        ),
      cell: info => {
        const c = info.row.original.coverage;
        return renderTableCell(
          fmtFacilityHomeDays(c.facilityEliminationPeriodDays, c.homeCareEliminationPeriodDays),
        );
      },
    }),
  ];
}

function buildProjectionsColumns(roiCalcType: ReturnOnInvestmentCalculationType) {
  const roiHeaderLabel = roiCalcType === 'total' ? 'ROI' : 'Annual ROI';

  return [
    columnHelper.accessor('label', {
      id: 'label',
      meta: { label: 'Option' },
      size: 240,
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Option
          </SortHeader>,
        ),
      cell: info => renderOptionLabelCell(info.row.original),
    }),
    columnHelper.accessor(r => r.derived?.policyTotalCost, {
      id: 'cost',
      meta: { label: 'Cost' },
      sortUndefined: 'last',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Cost
          </SortHeader>,
        ),
      cell: info =>
        renderTableCell(<span className="text-red-700">{fmtLtcCost(info.getValue())}</span>),
    }),
    columnHelper.accessor(r => ltcPayoutOf(r.derived), {
      id: 'ltcPayout',
      meta: { label: 'LTC Payout' },
      sortUndefined: 'last',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            LTC Payout
          </SortHeader>,
        ),
      cell: info =>
        renderTableCell(<span className="text-green-600">{fmtLtcCost(info.getValue())}</span>),
    }),
    // Non-LTC Payout / Total Payout are omitted for Genworth: trad-LTC has no death
    // benefit or cash value, so policyNonLtcPayout is always 0 and policyTotalValue
    // collapses to projectedCostCoverage (calcFundingSourceDerivatives.ts:2147-2149).
    // Re-introduce these columns when a hybrid carrier is supported.
    columnHelper.accessor(r => roiAccessor(r.derived, roiCalcType), {
      id: 'roi',
      meta: { label: roiHeaderLabel },
      sortUndefined: 'last',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            {roiHeaderLabel}
          </SortHeader>,
        ),
      cell: info => {
        const derived = info.row.original.derived;
        if (!derived) return renderTableCell('—');
        const formatted = formatROI(
          {
            projectedReturnOnInvestment: derived.policyProjectedReturnOnInvestment ?? 0,
            projectedCompoundAnnualGrowthRate: derived.policyProjectedCompoundAnnualGrowthRate ?? 0,
            projectedInternalRateOfReturn: derived.policyProjectedInternalRateOfReturn,
            investmentPeriodYears: derived.investmentPeriodYears ?? 0,
            isIrrSuspect: derived.isIrrSuspect,
          },
          roiCalcType,
        );
        const showIrrSuspect = roiCalcType === 'internalRateOfReturn' && derived.isIrrSuspect;
        if (showIrrSuspect) {
          return renderTableCell(
            <Tooltip
              title={IRR_SUSPECT_EXPLANATION}
              arrow
              placement="top"
              slotProps={{
                tooltip: { sx: { fontSize: '0.875rem', maxWidth: 320, padding: '8px 12px' } },
              }}
            >
              <span className="cursor-help underline decoration-dotted underline-offset-2">
                {IRR_SUSPECT_MESSAGE}
              </span>
            </Tooltip>,
          );
        }
        return renderTableCell(formatted);
      },
    }),
  ];
}

// ---------- helpers ----------

function ltcPayoutOf(derived: PolicyFundingSource | null): number | undefined {
  if (!derived) return undefined;
  // Pre-claim vs. on-claim: shop insurance uses projectedCostCoverage first; for clients
  // currently on claim, policyCoverageOfRemainingLtcCosts is set instead. ?? not || so
  // a legitimate 0 doesn't get swallowed.
  return derived.projectedCostCoverage ?? derived.policyCoverageOfRemainingLtcCosts;
}

function roiAccessor(
  derived: PolicyFundingSource | null,
  calcType: ReturnOnInvestmentCalculationType,
): number | undefined {
  if (!derived) return undefined;
  switch (calcType) {
    case 'compoundAnnualGrowthRate':
      return derived.policyProjectedCompoundAnnualGrowthRate;
    case 'internalRateOfReturn':
      return derived.policyProjectedInternalRateOfReturn;
    case 'total':
    default:
      return derived.policyProjectedReturnOnInvestment;
  }
}

function parseAppliedMap(raw: string | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function safeParse(json: string): GenworthRateActionLetterResults | null {
  try {
    return JSON.parse(json) as GenworthRateActionLetterResults;
  } catch {
    return null;
  }
}

function fmtCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function fmtCurrencyOrUnlimited(v: number | null | undefined, isUnlimited: boolean): string {
  if (isUnlimited) return 'Unlimited';
  return fmtCurrency(v);
}

function fmtLtcCost(v: number | null | undefined): React.ReactNode {
  if (v == null) return '—';
  return <LtcCostDisplay value={v} />;
}

function fmtYears(v: number | null | undefined): string {
  if (v == null) return '—';
  return v === 1 ? '1 year' : `${v} years`;
}

function fmtYearsOrUnlimited(v: number | null | undefined, isUnlimited: boolean): string {
  if (isUnlimited) return 'Unlimited';
  return fmtYears(v);
}

function fmtFacilityHomeDays(facility: number | null | undefined, home: number | null | undefined): string {
  if (facility == null && home == null) return '—';
  const f = facility == null ? '—' : String(facility);
  const h = home == null ? '—' : String(home);
  return `${f} / ${h} days`;
}

function fmtInflation(
  ip: GenworthRateActionLetterResults['options'][number]['inflationProtection'],
): string {
  if (!ip || ip.type === 'NONE') return 'None';
  const rate = ip.rate != null ? `${ip.rate}%` : '';
  const label = ip.type === 'COMPOUND' ? 'Comp' : ip.type === 'SIMPLE' ? 'Simp' : ip.type;
  return rate ? `${label} ${rate}` : label;
}

function formatInflationSortKey(
  ip: GenworthRateActionLetterResults['options'][number]['inflationProtection'],
): number {
  if (!ip || ip.type === 'NONE') return 0;
  return ip.rate ?? 0;
}

function fmtNiceDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  // ISO date-only strings are parsed as UTC midnight by `new Date`, which can shift back a
  // day in west-of-UTC zones. Parse the parts directly so the displayed date matches what's
  // on the letter.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
